import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSession } from '../auth/session.tsx';
import type { MapPlace } from '../api/types.ts';

/**
 * The caseload on a map.
 *
 * An ADDITIONAL view, never a replacement. The facility board (`view=locations`)
 * answers "which places are behind, worst first" and answers it well and fast;
 * this answers "where are they", which a list cannot. Both read the same
 * statement and the same clients — see `directorStatement` in
 * `api/src/routes/dashboards.ts`, where the map's source is chosen and the
 * reason is written down at length.
 *
 * ---------------------------------------------------------------------------
 *  THE TILES COME FROM OUR OWN API, AND THAT IS THE POINT
 * ---------------------------------------------------------------------------
 *
 * Pointing Leaflet at OpenStreetMap would tell a third-party tile server which
 * streets are being looked at — and on a screen covered in pins for
 * guardianship clients, that is a rough picture of where they live. It would
 * undo the reason Azure Maps was chosen for geocoding in the first place.
 *
 * So tiles come from `/api/tiles/...`, which fetches them from Azure Maps with
 * the App Service managed identity and caches them. Leaflet's own `TileLayer`
 * cannot be used as-is: it sets `img.src`, which sends no `Authorization`
 * header, so every tile would come back 401. `createTile` is overridden to
 * fetch the tile properly and hand the bytes to the image as a blob URL.
 *
 * **Every blob URL is revoked when Leaflet discards the tile.** Object URLs
 * live until revoked, so a map that is panned around for an afternoon would
 * otherwise hold every tile it had ever drawn — the same "only ever grows"
 * shape as the tile cache on disk, and the audit partitions before it.
 */

/** Where the map opens: the five boroughs and Nassau, which is the whole caseload. */
const DEFAULT_CENTRE: [number, number] = [40.7, -73.87];
const DEFAULT_ZOOM = 11;
/** Matched to the server's cap. Asking outside it is refused there, not here. */
const MIN_ZOOM = 9;
const MAX_ZOOM = 17;

/** The ends of the board's colour scale, so a colour means the same thing on both. */
const LOW_PACE = [255, 105, 180] as const;
const FULL_PACE = [173, 216, 230] as const;

function paceColour(averagePace: number | null): string {
  if (averagePace === null) return 'rgb(229, 229, 229)';
  const t = Math.min(Math.max(averagePace, 0), 1);
  const [r, g, b] = LOW_PACE.map((low, i) => Math.round(low + (FULL_PACE[i]! - low) * t));
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * A tile layer that authenticates.
 *
 * Built inside the component so it closes over the session's `getToken`, which
 * is a hook value and cannot be read from a module-level class.
 */
function authenticatedTiles(getToken: () => Promise<string>): L.TileLayer {
  const Authenticated = L.TileLayer.extend({
    createTile(this: L.TileLayer, coords: L.Coords, done: L.DoneCallback) {
      const img = document.createElement('img');
      img.alt = '';
      // Decorative: the map's meaning is in the pins, which are real DOM
      // elements with text. A screen reader announcing 40 tile images would
      // bury them.
      img.setAttribute('role', 'presentation');

      let objectUrl: string | null = null;
      // Leaflet discards tiles as they leave the viewport. Without this the
      // page holds every tile it has ever drawn, for the life of the tab.
      const revoke = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };
      img.addEventListener('load', () => {
        // Safe once decoded: the bitmap is the browser's now.
        if (img.complete) revoke();
      });

      void (async () => {
        try {
          const token = await getToken();
          const response = await fetch(
            `/api/tiles/microsoft.base.road/${coords.z}/${coords.x}/${coords.y}`,
            { headers: { authorization: `Bearer ${token}` } },
          );
          if (!response.ok) throw new Error(`tile ${response.status}`);
          const blob = await response.blob();
          objectUrl = URL.createObjectURL(blob);
          img.src = objectUrl;
          done(undefined, img);
        } catch (err) {
          revoke();
          // Leaflet leaves a gap where the tile would be, which is the right
          // outcome: a missing basemap tile must not take the pins with it.
          done(err as Error, img);
        }
      })();

      return img;
    },
  });
  return new (Authenticated as unknown as new (url: string, opts: L.TileLayerOptions) => L.TileLayer)(
    '',
    {
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      tileSize: 256,
      // Required by Azure Maps' terms and by simple good manners.
      attribution: '© Microsoft, © TomTom',
    },
  );
}

export function CaseloadMap({
  places,
  onOpenPlace,
}: {
  places: MapPlace[];
  onOpenPlace: (place: MapPlace) => void;
}) {
  const { getToken } = useSession();
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markers = useRef<L.LayerGroup | null>(null);
  const [failed, setFailed] = useState(false);

  // The token getter changes identity between renders; the layer must not be
  // rebuilt for that. A ref keeps the latest without re-running the effect.
  const token = useRef(getToken);
  token.current = getToken;

  const open = useRef(onOpenPlace);
  open.current = onOpenPlace;

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, {
      center: DEFAULT_CENTRE,
      zoom: DEFAULT_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      // Scroll-wheel zoom off by default: the map sits in a scrolling page, and
      // a wheel that zooms instead of scrolling traps the reader.
      scrollWheelZoom: false,
    });
    instance.addLayer(authenticatedTiles(() => token.current()));
    markers.current = L.layerGroup().addTo(instance);
    map.current = instance;

    return () => {
      instance.remove();
      map.current = null;
      markers.current = null;
    };
  }, []);

  // Pins follow the data. Rebuilt wholesale rather than diffed: a few hundred
  // circle markers is nothing, and diffing is where stale pins come from.
  useEffect(() => {
    const layer = markers.current;
    const instance = map.current;
    if (!layer || !instance) return;
    layer.clearLayers();

    const points: [number, number][] = [];
    for (const place of places) {
      const lat = Number(place.latitude);
      const lon = Number(place.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      points.push([lat, lon]);

      // Area scales with the number of clients, not radius — a place with four
      // people should look four times as big, and doubling the radius would
      // make it look sixteen.
      const radius = Math.min(26, 7 + Math.sqrt(place.client_count) * 3.2);

      const marker = L.circleMarker([lat, lon], {
        radius,
        // Never colour alone: the count is drawn in the tooltip and the
        // never-seen ring is a shape, not a hue.
        color: place.never_seen > 0 ? '#b91c1c' : '#404040',
        weight: place.never_seen > 0 ? 3 : 1,
        fillColor: paceColour(place.average_pace),
        fillOpacity: 0.85,
      });

      const name = place.location ?? 'Location not recorded';
      marker.bindTooltip(
        `<strong>${escapeHtml(name)}</strong><br>${place.client_count} client${
          place.client_count === 1 ? '' : 's'
        }${place.never_seen > 0 ? `<br><strong>${place.never_seen} never seen</strong>` : ''}`,
        { direction: 'top' },
      );
      marker.on('click', () => open.current(place));
      // Keyboard: a map nobody can tab through is a map half the staff cannot
      // use. Leaflet gives circle markers no focus by default.
      marker.on('keypress', (event) => {
        const original = (event as unknown as { originalEvent?: KeyboardEvent }).originalEvent;
        if (original?.key === 'Enter' || original?.key === ' ') open.current(place);
      });
      layer.addLayer(marker);
    }

    if (points.length > 0) {
      try {
        instance.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 14 });
      } catch {
        setFailed(true);
      }
    }
  }, [places]);

  return (
    <div className="relative">
      <div
        ref={container}
        className="h-[32rem] w-full rounded-lg bg-neutral-200 lg:h-[40rem]"
        // Leaflet needs a real height before it will lay out; the class above
        // provides it. Announced as an image region so a screen reader does not
        // walk hundreds of tiles.
        role="region"
        aria-label="Map of where clients are now"
      />
      {failed && (
        <p role="status" className="mt-2 text-sm text-amber-900">
          The map could not fit to these places. The list below is complete.
        </p>
      )}
      <button
        type="button"
        onClick={() => map.current?.scrollWheelZoom.enable()}
        className="absolute right-3 bottom-3 z-[500] min-h-[44px] rounded-lg bg-white px-3
                   text-sm font-semibold text-neutral-800 shadow ring-1 ring-neutral-300"
      >
        Enable scroll zoom
      </button>
    </div>
  );
}

/** Tooltips take HTML, and a place name is data. */
function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}
